import React from "react";
import { Autocomplete, TextField, Typography, Box } from "@mui/material";

// ── Component ────────────────────────────────────────────────────────────────

/**
 * SearchSelect — inline type-to-search single-select.
 *
 * Props:
 *   options       [{ id, label }]
 *   value         string | null       selected id
 *   onChange      (id, option) => {}
 *   placeholder   string
 *   loading       bool
 *   disabled      bool
 *   width         string | number     (default "100%")
 *   noOptionsText string
 */
const SearchSelect = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Select…",
  loading = false,
  disabled = false,
  width = "100%",
  noOptionsText = "No options found",
}) => {
  const selectedOption = options.find((o) => String(o.id) === String(value)) ?? null;

  const handleChange = (_, newVal) => {
    onChange?.(newVal?.id ?? null, newVal ?? null);
  };

  return (
    <Box sx={{ width }}>
      <Autocomplete
        options={options}
        getOptionLabel={(o) => o.label ?? ""}
        isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
        value={selectedOption}
        onChange={handleChange}
        loading={loading}
        disabled={disabled}
        noOptionsText={
          <Typography sx={{ fontSize: "13px", color: "#9B9B9B" }}>{noOptionsText}</Typography>
        }
        // In MUI v6, .MuiAutocomplete-endAdornment is a direct child of
        // .MuiAutocomplete-root, NOT inside .MuiOutlinedInput-root.
        // It must be targeted from the Autocomplete sx, not the TextField sx.
        sx={{
          "& .MuiAutocomplete-endAdornment": {
            top: "50% !important",
            transform: "translateY(-50%) !important",
          },
        }}
        slotProps={{
          paper: {
            sx: {
              mt: "4px",
              borderRadius: "8px",
              boxShadow: "0px 8px 24px rgba(0,0,0,0.10)",
              border: "1px solid #EBEBEB",
            },
          },
          listbox: {
            sx: {
              py: "4px",
              "& .MuiAutocomplete-option": {
                fontSize: "14px",
                color: "#1F2937",
                padding: "10px 16px",
                "&[aria-selected='true']": {
                  backgroundColor: "#F5F3FF",
                  color: "#4338CA",
                  fontWeight: 500,
                },
                "&.Mui-focused": {
                  backgroundColor: "#F9FAFB",
                },
                "&[aria-selected='true'].Mui-focused": {
                  backgroundColor: "#EDE9FE",
                },
              },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant="outlined"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: "13px",
                backgroundColor: "#FFFFFF",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D0D5DD",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4D44D8",
                },
                "&.Mui-focused": {
                  boxShadow: "0 0 0 3px rgba(77,68,216,0.08)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4D44D8",
                  borderWidth: "1px",
                },
                "& .MuiAutocomplete-input": {
                  fontSize: "13px",
                  color: "#1F2937",
                },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default SearchSelect;
